import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  CreditCard, 
  Users, 
  Building2, 
  DollarSign, 
  TrendingUp,
  AlertTriangle,
  Check,
  Edit,
  Calendar,
  FileText,
  Settings,
  Shield,
  Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPricingPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [editTierDialog, setEditTierDialog] = useState(false);
  const [editSubDialog, setEditSubDialog] = useState(false);
  const [overrideDialog, setOverrideDialog] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [overrideData, setOverrideData] = useState({
    trial_end: '',
    max_users: '',
    max_projects: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [tiersRes, subsRes, orgsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/pricing/tiers`, { headers }),
        fetch(`${API_URL}/api/admin/subscriptions`, { headers }),
        fetch(`${API_URL}/api/admin/organizations`, { headers })
      ]);

      // Check for errors
      if (!tiersRes.ok || !subsRes.ok || !orgsRes.ok) {
        throw new Error('Failed to fetch some data');
      }

      const [tiersData, subsData, orgsData] = await Promise.all([
        tiersRes.json(),
        subsRes.json(),
        orgsRes.json()
      ]);

      setTiers(Array.isArray(tiersData) ? tiersData : []);
      setSubscriptions(Array.isArray(subsData) ? subsData : []);
      setOrganizations(Array.isArray(orgsData) ? orgsData : []);
    } catch (err) {
      console.error('Failed to fetch pricing data:', err);
      toast.error('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTier = async (tierId, updates) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/pricing/tiers/${tierId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (!res.ok) throw new Error('Failed to update tier');
      
      toast.success('Pricing tier updated');
      setEditTierDialog(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to update tier');
    }
  };

  const handleUpdateSubscription = async (accountOwnerId, updates) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/subscriptions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          account_owner_id: accountOwnerId,
          ...updates
        })
      });

      if (!res.ok) throw new Error('Failed to update subscription');
      
      toast.success('Subscription updated');
      setEditSubDialog(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to update subscription');
    }
  };

  const handleSetOverrides = async () => {
    if (!selectedSub) return;
    
    try {
      const payload = {};
      if (overrideData.trial_end) payload.trial_end = new Date(overrideData.trial_end).toISOString();
      if (overrideData.max_users) payload.max_users = parseInt(overrideData.max_users);
      if (overrideData.max_projects) payload.max_projects = parseInt(overrideData.max_projects);
      if (overrideData.notes) payload.notes = overrideData.notes;

      const res = await fetch(`${API_URL}/api/admin/subscriptions/${selectedSub.account_owner_id}/overrides`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error('Failed to set overrides');
      
      toast.success('Account overrides updated');
      setOverrideDialog(false);
      setOverrideData({ trial_end: '', max_users: '', max_projects: '', notes: '' });
      fetchData();
    } catch (err) {
      toast.error('Failed to update overrides');
    }
  };

  const handleClearOverrides = async () => {
    if (!selectedSub) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/subscriptions/${selectedSub.account_owner_id}/overrides`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to clear overrides');
      
      toast.success('Overrides cleared');
      setOverrideDialog(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to clear overrides');
    }
  };

  const openOverrideDialog = (sub) => {
    setSelectedSub(sub);
    setOverrideData({
      trial_end: sub.trial_end_override ? sub.trial_end_override.split('T')[0] : (sub.trial_end ? sub.trial_end.split('T')[0] : ''),
      max_users: sub.max_users_override?.toString() || '',
      max_projects: sub.max_projects_override?.toString() || '',
      notes: sub.admin_notes || ''
    });
    setOverrideDialog(true);
  };

  // Calculate summary stats
  const stats = {
    totalAccountOwners: subscriptions.length,
    activeTrials: subscriptions.filter(s => s.status === 'trial').length,
    activePaid: subscriptions.filter(s => s.status === 'active' && s.tier_id !== 'free_trial').length,
    overLimit: subscriptions.filter(s => s.over_limit).length,
    monthlyRevenue: subscriptions
      .filter(s => s.tier_id !== 'free_trial')
      .reduce((sum, s) => sum + (s.paid_users || 0) * (s.price_per_user || 0), 0)
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-pricing-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Pricing & Subscriptions</h1>
        <p className="text-slate-500">Manage pricing tiers and Account Owner subscriptions</p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Account Owners</p>
                <p className="text-2xl font-bold">{stats.totalAccountOwners}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Calendar className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Active Trials</p>
                <p className="text-2xl font-bold">{stats.activeTrials}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-100 rounded-lg">
                <CreditCard className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Paid Subscriptions</p>
                <p className="text-2xl font-bold">{stats.activePaid}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Over Limit</p>
                <p className="text-2xl font-bold">{stats.overLimit}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Est. MRR</p>
                <p className="text-2xl font-bold">${stats.monthlyRevenue.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="subscriptions" className="space-y-4">
        <TabsList>
          <TabsTrigger value="subscriptions" className="gap-2">
            <Building2 className="w-4 h-4" />
            Subscriptions
          </TabsTrigger>
          <TabsTrigger value="tiers" className="gap-2">
            <Settings className="w-4 h-4" />
            Pricing Tiers
          </TabsTrigger>
        </TabsList>

        {/* Subscriptions Tab */}
        <TabsContent value="subscriptions">
          <Card>
            <CardHeader>
              <CardTitle>Account Owner Subscriptions</CardTitle>
              <CardDescription>View and manage subscriptions by Account Owner (Organization is an attribute)</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Account Owner</TableHead>
                    <TableHead>Organization</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Paid Users</TableHead>
                    <TableHead className="text-right">Current Users</TableHead>
                    <TableHead className="text-right">Overage</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscriptions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-slate-500 py-8">
                        No Account Owners found
                      </TableCell>
                    </TableRow>
                  ) : (
                    subscriptions.map(sub => (
                      <TableRow key={sub.id || sub.account_owner_id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{sub.account_owner_name || 'Unknown'}</div>
                            <div className="text-xs text-slate-500">{sub.account_owner_email}</div>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {sub.organization_name || 'Unknown'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{sub.tier_display_name || sub.tier_id}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant={sub.status === 'trial' ? 'secondary' : sub.status === 'active' ? 'default' : 'destructive'}
                            className={sub.status === 'active' ? 'bg-emerald-100 text-emerald-700' : ''}
                          >
                            {sub.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {sub.paid_users || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          {sub.current_users || 0}
                        </TableCell>
                        <TableCell className="text-right">
                          {sub.over_limit ? (
                            <span className="text-red-600 font-medium">
                              +{sub.overage_users} ({sub.overage_percentage}%)
                            </span>
                          ) : sub.overage_users > 0 ? (
                            <span className="text-amber-600">
                              +{sub.overage_users} ({sub.overage_percentage}%)
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setSelectedSub(sub);
                                setEditSubDialog(true);
                              }}
                              title="Edit subscription"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openOverrideDialog(sub)}
                              title="Set overrides"
                              className={sub.has_overrides ? 'text-violet-600' : ''}
                            >
                              <Shield className="w-4 h-4" />
                              {sub.has_overrides && <Sparkles className="w-3 h-3 absolute -top-1 -right-1 text-violet-500" />}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pricing Tiers Tab */}
        <TabsContent value="tiers">
          <Card>
            <CardHeader>
              <CardTitle>Pricing Tiers</CardTitle>
              <CardDescription>Configure pricing plans and features</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {tiers.map(tier => (
                  <Card key={tier.id} className={`relative ${!tier.is_active ? 'opacity-50' : ''}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">{tier.display_name}</CardTitle>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setSelectedTier(tier);
                            setEditTierDialog(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                      <CardDescription>
                        {tier.min_users === 0 
                          ? `Up to ${tier.max_users} users`
                          : tier.max_users 
                            ? `${tier.min_users}-${tier.max_users} users`
                            : `${tier.min_users}+ users`
                        }
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div>
                          <span className="text-3xl font-bold">
                            ${tier.price_per_user}
                          </span>
                          <span className="text-slate-500 text-sm">/user/mo</span>
                        </div>
                        
                        {tier.min_monthly_cost > 0 && (
                          <p className="text-sm text-slate-500">
                            Min: ${tier.min_monthly_cost}/month
                          </p>
                        )}
                        
                        <div className="pt-2 border-t space-y-1">
                          <p className="text-xs font-medium text-slate-500 uppercase">Features</p>
                          {tier.features.slice(0, 4).map((f, i) => (
                            <div key={i} className="flex items-center gap-1 text-sm text-slate-600">
                              <Check className="w-3 h-3 text-emerald-500" />
                              <span className="truncate">{f}</span>
                            </div>
                          ))}
                          {tier.features.length > 4 && (
                            <p className="text-xs text-slate-400">
                              +{tier.features.length - 4} more
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Tier Dialog */}
      <Dialog open={editTierDialog} onOpenChange={setEditTierDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Pricing Tier</DialogTitle>
            <DialogDescription>
              Update pricing and limits for {selectedTier?.display_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedTier && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Display Name</Label>
                  <Input 
                    value={selectedTier.display_name}
                    onChange={(e) => setSelectedTier({...selectedTier, display_name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Price per User ($)</Label>
                  <Input 
                    type="number"
                    value={selectedTier.price_per_user}
                    onChange={(e) => setSelectedTier({...selectedTier, price_per_user: parseFloat(e.target.value)})}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Min Users</Label>
                  <Input 
                    type="number"
                    value={selectedTier.min_users}
                    onChange={(e) => setSelectedTier({...selectedTier, min_users: parseInt(e.target.value)})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max Users (0 = unlimited)</Label>
                  <Input 
                    type="number"
                    value={selectedTier.max_users || 0}
                    onChange={(e) => setSelectedTier({...selectedTier, max_users: parseInt(e.target.value) || null})}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Min Monthly Cost ($)</Label>
                  <Input 
                    type="number"
                    value={selectedTier.min_monthly_cost}
                    onChange={(e) => setSelectedTier({...selectedTier, min_monthly_cost: parseFloat(e.target.value)})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max Projects (0 = unlimited)</Label>
                  <Input 
                    type="number"
                    value={selectedTier.max_projects || 0}
                    onChange={(e) => setSelectedTier({...selectedTier, max_projects: parseInt(e.target.value) || null})}
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="tier-active"
                  checked={selectedTier.is_active}
                  onChange={(e) => setSelectedTier({...selectedTier, is_active: e.target.checked})}
                  className="rounded"
                />
                <Label htmlFor="tier-active">Active</Label>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTierDialog(false)}>Cancel</Button>
            <Button onClick={() => handleUpdateTier(selectedTier.id, selectedTier)}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Subscription Dialog */}
      <Dialog open={editSubDialog} onOpenChange={setEditSubDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Subscription</DialogTitle>
            <DialogDescription>
              Update subscription for {selectedSub?.account_owner_name} ({selectedSub?.organization_name})
            </DialogDescription>
          </DialogHeader>
          
          {selectedSub && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Pricing Tier</Label>
                <Select 
                  value={selectedSub.tier_id}
                  onValueChange={(value) => setSelectedSub({...selectedSub, tier_id: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {tiers.map(tier => (
                      <SelectItem key={tier.id} value={tier.id}>
                        {tier.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Paid Users</Label>
                <Input 
                  type="number"
                  value={selectedSub.paid_users || 0}
                  onChange={(e) => setSelectedSub({...selectedSub, paid_users: parseInt(e.target.value)})}
                />
                <p className="text-xs text-slate-500">
                  Current usage: {selectedSub.current_users} users
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Status</Label>
                <Select 
                  value={selectedSub.status}
                  onValueChange={(value) => setSelectedSub({...selectedSub, status: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditSubDialog(false)}>Cancel</Button>
            <Button onClick={() => handleUpdateSubscription(selectedSub.account_owner_id, {
              tier_id: selectedSub.tier_id,
              paid_users: selectedSub.paid_users,
              status: selectedSub.status
            })}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Override Dialog */}
      <Dialog open={overrideDialog} onOpenChange={setOverrideDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-violet-600" />
              Account Overrides
            </DialogTitle>
            <DialogDescription>
              Set custom limits for {selectedSub?.account_owner_name} ({selectedSub?.organization_name})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Trial End Date Override */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-500" />
                Trial End Date
              </Label>
              <Input 
                type="date"
                value={overrideData.trial_end}
                onChange={(e) => setOverrideData({...overrideData, trial_end: e.target.value})}
              />
              <p className="text-xs text-slate-500">
                {selectedSub?.trial_end_override 
                  ? `Current override: ${new Date(selectedSub.trial_end_override).toLocaleDateString()}`
                  : selectedSub?.trial_end 
                    ? `Default: ${new Date(selectedSub.trial_end).toLocaleDateString()}`
                    : 'No trial end date set'
                }
              </p>
            </div>

            {/* Max Users Override */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-500" />
                Max Users Limit
              </Label>
              <Input 
                type="number"
                placeholder="Leave empty to use tier default"
                value={overrideData.max_users}
                onChange={(e) => setOverrideData({...overrideData, max_users: e.target.value})}
              />
              <p className="text-xs text-slate-500">
                {selectedSub?.max_users_override 
                  ? `Current override: ${selectedSub.max_users_override} users`
                  : `Using tier default (${selectedSub?.paid_users || 'unlimited'} users)`
                }
              </p>
            </div>

            {/* Max Projects Override */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-500" />
                Max Projects Limit
              </Label>
              <Input 
                type="number"
                placeholder="Leave empty to use tier default"
                value={overrideData.max_projects}
                onChange={(e) => setOverrideData({...overrideData, max_projects: e.target.value})}
              />
              <p className="text-xs text-slate-500">
                {selectedSub?.max_projects_override 
                  ? `Current override: ${selectedSub.max_projects_override} projects`
                  : 'Using tier default'
                }
              </p>
            </div>

            {/* Admin Notes */}
            <div className="space-y-2">
              <Label>Admin Notes</Label>
              <Textarea 
                placeholder="Internal notes about this override..."
                value={overrideData.notes}
                onChange={(e) => setOverrideData({...overrideData, notes: e.target.value})}
                rows={3}
              />
            </div>

            {/* Current Overrides Summary */}
            {selectedSub?.has_overrides && (
              <Card className="bg-violet-50 border-violet-200">
                <CardContent className="py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-violet-600" />
                      <span className="text-sm font-medium text-violet-700">Active Overrides</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleClearOverrides}
                      className="text-violet-600 hover:text-violet-700 hover:bg-violet-100"
                    >
                      Clear All
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSetOverrides} className="bg-violet-600 hover:bg-violet-700">
              <Check className="w-4 h-4 mr-2" />
              Save Overrides
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
